/**
 * XAES Analyst chat state machine, lifted to multi-session storage.
 *
 * Live buffer state lives in `chat-runtime-store` (SPA memory) so
 * remounting the panel — sidebar ↔ XAES-AI modal, or navigating away
 * and back — keeps the current session for that user + scope. A page
 * refresh clears SPA memory; on hydrate we auto-resume the newest
 * IndexedDB session so chats survive login/logout. History keys are
 * per-user (see `persistence.ts`).
 *
 * The session only lands on disk once the user sends their first
 * message (empty sessions are noise).
 *
 * Source references:
 *   - Loveable v1 reference: `src/hooks/use-xaes-llm.tsx` — single
 *     in-memory chat with no persistence.
 *   - Predecessor: this file held React-local state that cleared on
 *     every remount. Multi-session + per-user persistence in the
 *     chat overhaul; legacy data migrates via `persistence.ts`.
 *   - ADR 0001 — Vercel AI SDK streaming + prompt caching.
 *   - StrictMode-safe abort handling per CLAUDE.md conventions.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isAgentChatBackend, isClaudeConfigured } from "@/env";
import { useClaudeSettings } from "@/stores/useClaudeSettings";
import {
  buildSystemPrompt,
  type ChatScope,
} from "./context-builder";
import {
  buildRetrievedContext,
  buildWorkshopRegistersContext,
  ensureAnalystContextHydrated,
} from "./auto-retrieval";
import { buildWorkshopContentContext, getReferencedOutputIds } from "./workshop-content-context";
import {
  abortScope,
  chatScopeKey,
  EMPTY_CHAT_SCOPE_SLICE,
  getAbortController,
  hasHydratedSessions,
  markSessionsHydrated,
  markSessionsHydrating,
  releaseSessionsHydrating,
  setAbortController,
  useChatRuntimeStore,
  type ChatMessage,
  type ChatStatus,
} from "./chat-runtime-store";
import {
  clearAllSessions,
  deleteSession as deleteSessionStore,
  loadSessions,
  newSessionId,
  saveSession,
  titleFromFirstMessage,
  type ChatSession,
} from "./persistence";
import {
  defaultContextReferences,
  resolveReferences,
  type XaesReference,
} from "./reference-resolver";
import { streamXaesReply } from "./vercel-ai";
import { streamAgentReply, type AgentSource } from "./agent-api";
import type { CoreMessage } from "ai";
export type { ChatMessage, ChatStatus };

/** Selected at module load (build-time flag): when the chat backend is the
 *  AIW A2A agent, the SPA streams via the BFF instead of calling Anthropic
 *  directly. See plans/AIW_AGENT_CHAT_INTEGRATION.md. */
const AGENT_BACKEND = isAgentChatBackend();

export type UseChatArgs = {
  /** Conversation scope key — `"MC"` for Mission Control or a workshop id. */
  workshopId: ChatScope;
  /**
   * Signed-in username — namespaces both the live buffer and IndexedDB
   * history so users on a shared machine don't see each other's chats.
   * Prefer the stable OIDC `sub` when available. Falls back to `"anon"`
   * only when auth has no user yet.
   */
  userId: string;
  /**
   * Optional prior key to claim on first hydrate (e.g. preferred_username
   * when `userId` is now the OIDC subject). Ignored when equal to `userId`.
   */
  claimFromUserId?: string;
};

export type UseChatResult = {
  messages: ChatMessage[];
  status: ChatStatus;
  /** Whether `VITE_ANTHROPIC_API_KEY` is set. False ⇒ mock mode. */
  configured: boolean;
  /** Human-readable error from the last failed send, or `null` when ok. */
  error: string | null;
  /** Send a user turn with optional `@`-mention references. */
  send: (text: string, references?: XaesReference[]) => Promise<void>;
  /** Re-send the last user turn after an error. */
  retry: () => void;
  /** Abort the in-flight stream (no-op when idle). */
  abort: () => void;
  /** Wipe history for this user+scope (drops every session). */
  reset: () => Promise<void>;
  /** Stable id of the in-memory session, or `null` until the first
   *  message is sent (empty sessions are not persisted). */
  activeSessionId: string | null;
  /** Currently-active session as a snapshot — useful for the export
   *  flow (lets callers serialise without re-deriving title/timestamps).
   *  `null` until the first message is sent. */
  activeSession: ChatSession | null;
  /** Past sessions for this scope, sorted newest-first. */
  sessions: ChatSession[];
  /** Whether the sessions list has finished its initial IDB read.
   *  The UI uses this to gate the history dropdown empty state. */
  sessionsHydrated: boolean;
  /** Load a saved session into the buffer (drops the in-memory one
   *  if it's empty / unpersisted). No-op if the id is unknown. */
  loadSession: (sessionId: string) => void;
  /** Discard the current buffer and start a fresh blank session. */
  startNewSession: () => void;
  /** Drop a saved session from storage. If the deleted session was
   *  active, the panel falls back to a fresh blank one. */
  deleteSession: (sessionId: string) => Promise<void>;
  /**
   * Compose-bar context tags for the live session. Persist across
   * messages until the user adds/removes them or starts a new chat.
   */
  contextReferences: XaesReference[];
  /** Replace the session context tags (add/remove chips in the bar). */
  setContextReferences: (references: XaesReference[]) => void;
};

/** Tiny message-id generator — collision-resistant enough for chat. */
function newMessageId(): string {
  return `msg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Matches inline citation markers like `[1]`, `[12]` in the agent's reply. */
const CITATION_RE = /\[(\d+)\]/g;

/** Extract the set of source indices the agent cited inline as `[n]`, keeping
 *  only those that map to a retrieved source. Returns a sorted, de-duplicated
 *  list. Out-of-range or zero markers are ignored — the agent occasionally
 *  emits a bracketed number that isn't a grounding reference (ADO #460).
 *  Exported for unit testing. */
export function citedSourceIndices(text: string, sources: AgentSource[]): number[] {
  if (!sources.length) return [];
  const valid = new Set(sources.map((s) => s.index));
  const cited = new Set<number>();
  for (const m of text.matchAll(CITATION_RE)) {
    const n = Number(m[1]);
    if (valid.has(n)) cited.add(n);
  }
  return [...cited].sort((a, b) => a - b);
}

/** Convert our `ChatMessage[]` history into `CoreMessage[]` for the
 *  AI SDK. Drops streaming, errored, and empty-text entries.
 *
 *  Empty text entries are a real risk: an aborted send leaves the
 *  in-flight assistant bubble in the history with `streaming: false`
 *  but no text. Anthropic rejects any text content block whose value
 *  is empty ("messages: text content blocks must be non-empty"), so
 *  we strip them before sending. */
function toCoreMessages(history: ChatMessage[]): CoreMessage[] {
  const cleaned = history.filter(
    (m) => !m.streaming && !m.error && m.text.trim().length > 0,
  );
  // Drop leading assistant turns — Anthropic requires the first turn
  // to be user.
  let i = 0;
  while (i < cleaned.length && cleaned[i]?.role === "assistant") i++;
  const trimmed = cleaned.slice(i);
  return trimmed.map<CoreMessage>((m) => ({
    role: m.role,
    content: m.text,
  }));
}

export function useChat({
  workshopId,
  userId,
  claimFromUserId,
}: UseChatArgs): UseChatResult {
  const key = chatScopeKey(userId, workshopId);
  const ensure = useChatRuntimeStore((s) => s.ensure);
  const patch = useChatRuntimeStore((s) => s.patch);
  const setMessages = useChatRuntimeStore((s) => s.setMessages);
  const clearBuffer = useChatRuntimeStore((s) => s.clearBuffer);
  const resetSlice = useChatRuntimeStore((s) => s.resetSlice);
  const getSlice = useChatRuntimeStore((s) => s.getSlice);

  const slice = useChatRuntimeStore(
    (s) => s.byKey[key] ?? EMPTY_CHAT_SCOPE_SLICE,
  );

  // Configured flag is captured at mount; the env-injected key doesn't
  // change at runtime so a useState snapshot is enough. In agent-backend
  // mode there's no browser key — the BFF holds auth — so the chat is
  // always "configured" (never falls back to the mock reply).
  const [configured] = useState<boolean>(
    AGENT_BACKEND || isClaudeConfigured(),
  );
  const model = useClaudeSettings((s) => s.model);

  // Last send args for retry — per hook instance is fine; a remount
  // mid-error just loses the retry target, which is acceptable.
  const lastSendRef = useRef<{ text: string; references?: XaesReference[] } | null>(
    null,
  );

  // Ensure the slice exists, seed default workshop context once, then
  // hydrate the History list once per page lifetime for this user+scope.
  // Never clear the live buffer here — remounts and window swaps must
  // keep the current session (and its context tags).
  useEffect(() => {
    ensure(key);
    const current = getSlice(key);
    if (!current.contextSeeded) {
      patch(key, {
        contextReferences: defaultContextReferences(workshopId),
        contextSeeded: true,
      });
    }
    if (hasHydratedSessions(key)) {
      // Slice may have been created empty before hydration finished on
      // a previous mount — just sync the hydrated flag if needed.
      if (!getSlice(key).sessionsHydrated) {
        patch(key, { sessionsHydrated: true });
      }
      return;
    }
    if (!markSessionsHydrating(key)) return;
    let cancelled = false;
    void loadSessions(userId, workshopId, claimFromUserId).then((loaded) => {
      if (cancelled) return;
      const currentAfterLoad = getSlice(key);
      // Auto-resume the newest session when the live buffer is still
      // blank (fresh login / page reload). Skip if the user already
      // has an in-flight or remounted conversation in memory.
      const shouldResume =
        loaded.length > 0 &&
        currentAfterLoad.messages.length === 0 &&
        currentAfterLoad.activeSessionId === null;
      if (shouldResume) {
        const newest = loaded[0]!;
        const lastRefs = [...newest.messages]
          .reverse()
          .find(
            (m) =>
              m.role === "user" && m.references && m.references.length > 0,
          )?.references;
        patch(key, {
          sessions: loaded,
          sessionsHydrated: true,
          messages: newest.messages,
          activeSessionId: newest.id,
          activeCreatedAt: newest.createdAt,
          activeTitle: newest.title,
          status: "idle",
          error: null,
          contextReferences:
            lastRefs ?? defaultContextReferences(workshopId),
          contextSeeded: true,
        });
      } else {
        patch(key, { sessions: loaded, sessionsHydrated: true });
      }
      markSessionsHydrated(key);
    });
    return () => {
      cancelled = true;
      // StrictMode double-invoke and sidebar↔modal swaps unmount before
      // the IDB read settles. Release the lock immediately so the next
      // mount can hydrate; the live message buffer is untouched.
      releaseSessionsHydrating(key);
    };
  }, [key, userId, workshopId, claimFromUserId, ensure, patch, getSlice]);

  // Prefetch register/content stores on mount. send() also awaits hydration
  // before building the register dump so the first turn is never empty.
  useEffect(() => {
    void ensureAnalystContextHydrated(workshopId);
  }, [workshopId]);

  // Persist the active session whenever its messages change — only
  // after it's been promoted (i.e. the user has sent their first
  // message and we've assigned an id). Empty buffers stay in memory.
  const {
    messages,
    activeSessionId,
    activeCreatedAt,
    activeTitle,
    sessionsHydrated,
  } = slice;

  useEffect(() => {
    if (!sessionsHydrated && !hasHydratedSessions(key)) return;
    if (!activeSessionId || activeCreatedAt === null) return;
    const cleaned = messages.filter(
      (m) => !m.streaming && !m.error && m.text.trim().length > 0,
    );
    if (cleaned.length === 0) return;
    const now = Date.now();
    const session: ChatSession = {
      id: activeSessionId,
      workshopId,
      title: activeTitle,
      createdAt: activeCreatedAt,
      updatedAt: now,
      messages,
    };
    // Optimistically update the local list so the dropdown reflects
    // the latest `updatedAt` without a round-trip through IDB. The
    // store write happens in the background.
    const prevSessions = getSlice(key).sessions;
    const next = prevSessions.filter((s) => s.id !== session.id);
    next.unshift(session);
    next.sort((a, b) => b.updatedAt - a.updatedAt);
    patch(key, { sessions: next });
    void saveSession(userId, session);
  }, [
    key,
    userId,
    workshopId,
    messages,
    activeSessionId,
    activeCreatedAt,
    activeTitle,
    sessionsHydrated,
    getSlice,
    patch,
  ]);

  // Note: we do NOT abort on component unmount. The buffer is shared
  // across sidebar/modal remounts; aborting here would kill an in-
  // flight reply the moment the user opens or closes the XAES-AI window.

  const abort = useCallback(() => {
    abortScope(key);
    // Strip the streaming flag off the in-flight assistant bubble so
    // the UI stops blinking. We don't drop the message — the partial
    // text the user already saw is preserved.
    setMessages(key, (prev) =>
      prev.map((m) => (m.streaming ? { ...m, streaming: false } : m)),
    );
    patch(key, { status: "idle" });
  }, [key, setMessages, patch]);

  const send = useCallback(
    async (text: string, references?: XaesReference[]): Promise<void> => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const current = getSlice(key);
      if (current.status === "streaming" || current.status === "thinking") return;

      lastSendRef.current = { text: trimmed, references };
      patch(key, { error: null });

      // First user message in a fresh buffer promotes the session —
      // generate the id, capture the createdAt, and pin the title.
      let sessionId = current.activeSessionId;
      let createdAt = current.activeCreatedAt;
      let title = current.activeTitle;
      if (!sessionId) {
        sessionId = newSessionId();
        createdAt = Date.now();
        title = titleFromFirstMessage(trimmed);
        patch(key, {
          activeSessionId: sessionId,
          activeCreatedAt: createdAt,
          activeTitle: title,
        });
      }

      const userMsg: ChatMessage = {
        id: newMessageId(),
        role: "user",
        text: trimmed,
        references,
      };
      const pending = [...current.messages, userMsg];
      setMessages(key, pending);
      patch(key, { status: "thinking" });

      const resolved = references?.length
        ? await resolveReferences(references).catch(() => [])
        : [];

      await ensureAnalystContextHydrated(workshopId);

      const registersContext = buildWorkshopRegistersContext(workshopId);
      const contentContext = buildWorkshopContentContext(workshopId);
      const retrievedContext = await buildRetrievedContext(workshopId, trimmed);
      const autoContext = [registersContext, contentContext, retrievedContext]
        .filter(Boolean)
        .join("\n\n");

      const system = buildSystemPrompt({
        scope: workshopId,
        resolvedReferences: resolved,
        autoContext,
      });
      const core = toCoreMessages(pending);

      const controller = new AbortController();
      setAbortController(key, controller);

      const assistantId = newMessageId();
      setMessages(key, (prev) => [
        ...prev,
        { id: assistantId, role: "assistant", text: "", streaming: true },
      ]);

      try {
        const stream = AGENT_BACKEND
          ? streamAgentReply({
              system,
              messages: core,
              signal: controller.signal,
              onSources: (sources, grounding) =>
                setMessages(key, (prev) =>
                  prev.map((m) =>
                    m.id === assistantId
                      ? { ...m, sources, ...(grounding ? { grounding } : {}) }
                      : m,
                  ),
                ),
              // ADO #689 — chat-interaction telemetry attribution; "MC" scope
              // isn't one workshop, so both are omitted for it.
              ...(workshopId !== "MC" ? { workshopId } : {}),
              referencedOutputIds: getReferencedOutputIds(workshopId),
            })
          : streamXaesReply({
              system,
              messages: core,
              signal: controller.signal,
              model,
            });

        let firstDelta = true;
        for await (const delta of stream) {
          if (controller.signal.aborted) break;
          if (firstDelta) {
            patch(key, { status: "streaming" });
            firstDelta = false;
          }
          setMessages(key, (prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, text: m.text + delta } : m,
            ),
          );
        }

        if (controller.signal.aborted) {
          setMessages(key, (prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, streaming: false } : m,
            ),
          );
          patch(key, { status: "idle" });
          return;
        }

        setMessages(key, (prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  streaming: false,
                  citedIndices: m.sources
                    ? citedSourceIndices(m.text, m.sources)
                    : undefined,
                }
              : m,
          ),
        );
        patch(key, { status: "idle" });
      } catch (err) {
        if (controller.signal.aborted) {
          setMessages(key, (prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, streaming: false } : m,
            ),
          );
          patch(key, { status: "idle" });
          return;
        }
        const msg = err instanceof Error ? err.message : String(err);
        setMessages(key, (prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, streaming: false, error: msg }
              : m,
          ),
        );
        patch(key, { status: "error", error: msg });
      } finally {
        if (getAbortController(key) === controller) {
          setAbortController(key, null);
        }
      }
    },
    [key, workshopId, model, getSlice, patch, setMessages],
  );

  const retry = useCallback(() => {
    const last = lastSendRef.current;
    if (!last) return;
    const cleaned = [...getSlice(key).messages];
    while (cleaned.length && cleaned[cleaned.length - 1]?.error) cleaned.pop();
    const tail = cleaned[cleaned.length - 1];
    if (tail && tail.role === "user" && tail.text === last.text) {
      cleaned.pop();
    }
    setMessages(key, cleaned);
    patch(key, { error: null, status: "idle" });
    void send(last.text, last.references);
  }, [key, getSlice, setMessages, patch, send]);

  const reset = useCallback(async () => {
    abortScope(key);
    resetSlice(key);
    // Keep the hydrated marker so we don't re-claim legacy keys on every
    // reset; the IDB list is empty after clearAllSessions. Re-seed the
    // open-workshop context tag for the fresh buffer.
    markSessionsHydrated(key);
    patch(key, {
      sessionsHydrated: true,
      sessions: [],
      contextReferences: defaultContextReferences(workshopId),
      contextSeeded: true,
    });
    await clearAllSessions(userId, workshopId);
  }, [key, userId, workshopId, resetSlice, patch]);

  const loadSession = useCallback(
    (sessionId: string) => {
      const target = getSlice(key).sessions.find((s) => s.id === sessionId);
      if (!target) return;
      abortScope(key);
      setMessages(key, target.messages);
      // Restore context tags from the last user turn that carried any;
      // fall back to the open-workshop default so the bar isn't empty.
      const lastRefs = [...target.messages]
        .reverse()
        .find((m) => m.role === "user" && m.references && m.references.length > 0)
        ?.references;
      patch(key, {
        activeSessionId: target.id,
        activeCreatedAt: target.createdAt,
        activeTitle: target.title,
        status: "idle",
        error: null,
        contextReferences: lastRefs ?? defaultContextReferences(workshopId),
        contextSeeded: true,
      });
    },
    [key, workshopId, getSlice, setMessages, patch],
  );

  const startNewSession = useCallback(() => {
    abortScope(key);
    clearBuffer(key);
    patch(key, {
      contextReferences: defaultContextReferences(workshopId),
      contextSeeded: true,
    });
  }, [key, workshopId, clearBuffer, patch]);

  const deleteSession = useCallback(
    async (sessionId: string) => {
      const current = getSlice(key);
      const nextSessions = current.sessions.filter((s) => s.id !== sessionId);
      patch(key, { sessions: nextSessions });
      if (current.activeSessionId === sessionId) {
        abortScope(key);
        clearBuffer(key);
        // clearBuffer keeps sessions — restore the filtered list and
        // re-seed the open-workshop context tag.
        patch(key, {
          sessions: nextSessions,
          contextReferences: defaultContextReferences(workshopId),
          contextSeeded: true,
        });
      }
      await deleteSessionStore(userId, workshopId, sessionId);
    },
    [key, userId, workshopId, getSlice, patch, clearBuffer],
  );

  const setContextReferences = useCallback(
    (references: XaesReference[]) => {
      patch(key, { contextReferences: references, contextSeeded: true });
    },
    [key, patch],
  );

  const activeSession: ChatSession | null = useMemo(() => {
    if (!slice.activeSessionId || slice.activeCreatedAt === null) return null;
    return {
      id: slice.activeSessionId,
      workshopId,
      title: slice.activeTitle,
      createdAt: slice.activeCreatedAt,
      updatedAt: Date.now(),
      messages: slice.messages,
    };
  }, [
    slice.activeSessionId,
    slice.activeCreatedAt,
    slice.activeTitle,
    workshopId,
    slice.messages,
  ]);

  return useMemo(
    () => ({
      messages: slice.messages,
      status: slice.status,
      configured,
      error: slice.error,
      send,
      retry,
      abort,
      reset,
      activeSessionId: slice.activeSessionId,
      activeSession,
      sessions: slice.sessions,
      sessionsHydrated: slice.sessionsHydrated || hasHydratedSessions(key),
      loadSession,
      startNewSession,
      deleteSession,
      contextReferences: slice.contextReferences,
      setContextReferences,
    }),
    [
      slice.messages,
      slice.status,
      slice.error,
      slice.activeSessionId,
      slice.sessions,
      slice.sessionsHydrated,
      slice.contextReferences,
      configured,
      send,
      retry,
      abort,
      reset,
      activeSession,
      loadSession,
      startNewSession,
      deleteSession,
      setContextReferences,
      key,
    ],
  );
}
